import { supabase } from '@/lib/supabase';
import { ADMIN_EMAIL, ADMIN_NAME, SUPPORT_EMAIL } from '@/config/admin';

const STORAGE_KEY = 'izipay_admin_session';
const SETTINGS_ID = 'default';
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type AdminSession = {
  email: string;
  name: string;
};

export type AdminLoginResult =
  | { ok: true; session: AdminSession }
  | { ok: false; reason: 'empty' | 'invalid' | 'error' };

export type ChangePasswordResult =
  | { ok: true }
  | { ok: false; reason: 'current' | 'empty' | 'error' };

export type ChangeEmailResult =
  | { ok: true }
  | { ok: false; reason: 'current' | 'empty' | 'invalid' | 'error' };

type AdminSettings = {
  email: string;
  password: string;
  supportEmail: string;
};

async function readSettings(): Promise<AdminSettings | null> {
  const { data, error } = await supabase
    .from('admin_settings')
    .select('email, password, support_email')
    .eq('id', SETTINGS_ID)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const row = data as { email?: string; password?: string; support_email?: string };
  return {
    email: row.email ?? ADMIN_EMAIL,
    password: row.password ?? '',
    supportEmail: row.support_email ?? SUPPORT_EMAIL,
  };
}

export async function adminLogin(email: string, password: string): Promise<AdminLoginResult> {
  const trimmedEmail = email.trim().toLowerCase();
  const trimmedPassword = password.trim();

  if (!trimmedEmail || !trimmedPassword) {
    return { ok: false, reason: 'empty' };
  }

  let settings: AdminSettings | null = null;
  try {
    settings = await readSettings();
  } catch {
    return { ok: false, reason: 'error' };
  }

  const expectedEmail = (settings?.email || ADMIN_EMAIL).toLowerCase();
  const expectedPassword = settings?.password ?? '';

  if (trimmedEmail !== expectedEmail || trimmedPassword !== expectedPassword) {
    return { ok: false, reason: 'invalid' };
  }

  const session: AdminSession = { email: settings?.email || ADMIN_EMAIL, name: ADMIN_NAME };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* stockage indisponible */
  }

  return { ok: true, session };
}

export async function adminChangePassword(
  currentPassword: string,
  newPassword: string,
): Promise<ChangePasswordResult> {
  const trimmedCurrent = currentPassword.trim();
  const trimmedNew = newPassword.trim();

  if (!trimmedCurrent || !trimmedNew) {
    return { ok: false, reason: 'empty' };
  }

  try {
    const settings = await readSettings();
    const expected = settings?.password ?? '';
    if (trimmedCurrent !== expected) {
      return { ok: false, reason: 'current' };
    }

    const { error } = await supabase
      .from('admin_settings')
      .update({ password: trimmedNew, updated_at: new Date().toISOString() })
      .eq('id', SETTINGS_ID);

    if (error) throw error;
    return { ok: true };
  } catch {
    return { ok: false, reason: 'error' };
  }
}

/** Modifie l'adresse e-mail de connexion de l'administrateur (mot de passe requis). */
export async function adminUpdateEmail(
  currentPassword: string,
  newEmail: string,
): Promise<ChangeEmailResult> {
  const trimmedPassword = currentPassword.trim();
  const trimmedEmail = newEmail.trim();

  if (!trimmedPassword || !trimmedEmail) {
    return { ok: false, reason: 'empty' };
  }
  if (!emailPattern.test(trimmedEmail)) {
    return { ok: false, reason: 'invalid' };
  }

  try {
    const settings = await readSettings();
    const expected = settings?.password ?? '';
    if (trimmedPassword !== expected) {
      return { ok: false, reason: 'current' };
    }

    const { error } = await supabase
      .from('admin_settings')
      .update({ email: trimmedEmail, updated_at: new Date().toISOString() })
      .eq('id', SETTINGS_ID);

    if (error) throw error;
    return { ok: true };
  } catch {
    return { ok: false, reason: 'error' };
  }
}

/** Enregistre l'adresse e-mail de support affichée aux clients. */
export async function adminUpdateSupportEmail(
  newEmail: string,
): Promise<{ ok: boolean }> {
  const trimmedEmail = newEmail.trim();
  if (!trimmedEmail || !emailPattern.test(trimmedEmail)) {
    return { ok: false };
  }

  try {
    const { error } = await supabase
      .from('admin_settings')
      .update({ support_email: trimmedEmail, updated_at: new Date().toISOString() })
      .eq('id', SETTINGS_ID);

    if (error) throw error;
    return { ok: true };
  } catch {
    return { ok: false };
  }
}

/** Lit l'adresse e-mail de support configurée (ou la valeur de repli). */
export async function getSupportEmail(): Promise<string> {
  try {
    const settings = await readSettings();
    return settings?.supportEmail || SUPPORT_EMAIL;
  } catch {
    return SUPPORT_EMAIL;
  }
}

/** Met à jour l'e-mail conservé dans la session administrateur locale. */
export function updateAdminSessionEmail(email: string): AdminSession | null {
  const current = getAdminSession();
  if (!current) return null;
  const next: AdminSession = { ...current, email: email.trim() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* stockage indisponible */
  }
  return next;
}

export function getAdminSession(): AdminSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AdminSession;
  } catch {
    return null;
  }
}

export function adminLogout(): void {
  localStorage.removeItem(STORAGE_KEY);
}